import { isIOS, isPhone } from "@/lib/platform";
import { type PerfProfile, type PerfTier, profileForTier } from "./perf-profile";
import { PerfProfileStore } from "./perf-profile-store";

const USER_OVERRIDE_STORAGE_KEY = "virtuosa.perf.tier";

/**
 * Heuristic device-capability detection. Combines a CPU signal
 * (`navigator.hardwareConcurrency`, available everywhere including Safari iOS)
 * with a display-load signal (CSS pixels × DPR).
 *
 * Intentionally **does not** sniff iOS version or device model — Apple ships
 * a new SoC every year and that maintenance burden never pays off. We rely
 * on capability primitives that age well.
 *
 * **iOS is capped at `balanced` automatically** because:
 * 1. Safari has a hard WebAudio buffer ceiling (~400 MB → ~1 GB depending
 *    on device + iOS version) that we can't probe from JS, so the
 *    `lazy` Salamander strategy is never safe to opt into blindly.
 * 2. iPad 2018 (A12X, 8 cores) reports the same `hardwareConcurrency` as
 *    iPad Pro M4 (8+ cores), and we have no reliable way to tell them
 *    apart. Capping to `balanced` keeps both safe; the user explicitly
 *    forces `high` from the settings if their device can handle it.
 *
 * **Les téléphones aussi, Android compris** (2026-09-29). Un Android à huit
 * cœurs montait en `high`, donc en sons `lazy` : chaque nuance jouée dont la
 * couche n'était pas chargée en déclenchait le chargement — une trentaine de
 * MP3 à décoder, ~150 Mo, **pendant la lecture**. La première lecture d'un
 * morceau ramait, caméras d'un replay comprises ; arrêter puis relancer ne
 * ramait plus, tout étant chargé. S'y ajoutaient la réverbération et le
 * tampon de sortie le plus court. Un téléphone s'arrête donc à `balanced`,
 * comme l'iPhone — une couche préchargée, pas de réverbération — et le réglage
 * de performance reste là pour qui veut plus.
 */
export function detectPerfTier(): PerfTier {
  if (typeof navigator === "undefined") {
    return "balanced";
  }

  const cores = navigator.hardwareConcurrency ?? 4;
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const screenPixels = typeof window !== "undefined"
    ? window.screen.width * window.screen.height * dpr * dpr
    : 0;
  const onIOS = isIOS();
  const phone = isPhone();

  if (cores <= 4) {
    return "low";
  }

  if (onIOS || phone) {
    if (cores <= 6 && screenPixels > 5_000_000) {
      return "low";
    }

    return "balanced";
  }

  if (cores >= 8) {
    return "high";
  }

  return "balanced";
}

function readUserOverride(): PerfTier | null {
  if (typeof localStorage === "undefined") {
    return null;
  }

  try {
    const raw = localStorage.getItem(USER_OVERRIDE_STORAGE_KEY);

    if (raw === "low" || raw === "balanced" || raw === "high") {
      return raw;
    }
  } catch {
    // Storage may throw in private mode / disabled cookies.
  }

  return null;
}

function buildInitialProfile(): PerfProfile {
  const override = readUserOverride();

  if (override) {
    return profileForTier(override, "user-forced");
  }

  return profileForTier(detectPerfTier(), "auto");
}

let storeInstance: PerfProfileStore | null = null;

/**
 * Singleton accessor for the active perf-profile store. Built lazily on the
 * first call (so SSR/test environments without `navigator` don't blow up at
 * import time). Every consumer that reads or subscribes to perf goes through
 * this — there is exactly one source of truth per page.
 */
export function getPerfProfileStore(): PerfProfileStore {
  if (!storeInstance) {
    storeInstance = new PerfProfileStore(buildInitialProfile());
  }

  return storeInstance;
}

/**
 * Read the current profile. Kept as a thin shim because lots of legacy call
 * sites and constructors only need a snapshot. Reactive consumers should use
 * `getPerfProfileStore().subscribe(...)` instead.
 */
export function resolvePerfProfile(): PerfProfile {
  return getPerfProfileStore().value;
}

/** Test/dev hook to drop the singleton store between runs. */
export function clearPerfProfileCache(): void {
  storeInstance = null;
}

/**
 * Persist a manual tier override and update the live store so subscribers
 * (and the next reload) pick it up. Pass `null` to clear and fall back to
 * the auto-detected tier.
 */
export function setUserPerfOverride(tier: PerfTier | null): void {
  if (typeof localStorage !== "undefined") {
    try {
      if (tier === null) {
        localStorage.removeItem(USER_OVERRIDE_STORAGE_KEY);
      } else {
        localStorage.setItem(USER_OVERRIDE_STORAGE_KEY, tier);
      }
    } catch {
      // Same private-mode caveat as the read path.
    }
  }

  const store = getPerfProfileStore();

  if (tier === null) {
    store.setBase(detectPerfTier(), "auto");
  } else {
    store.setBase(tier, "user-forced");
  }
}
